import { Tag } from 'antd';
import { ClockCircleOutlined, FieldTimeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import BaseCellText from 'common/components/table/base-cell-text';
import { ICellRendererProps } from 'common/components/table/table.types';
import { DATE_SHORT_MMM_DD, DATE_SHORT_MMM_DD_YYYY } from 'common/constans/date.constants';
import {
  RentalReservationFeeTypeEnum,
  rentalReservationFeeTypeColorMap,
  rentalReservationFeeTypePriorityMap,
} from 'common/enums/rental-reservation-fee-type.enum';
import { RentalReservationStatusEnum, rentalReservationStatusColorMap } from 'common/enums/rental-reservation-status.enum';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatRentalPropertyAgencyLabel } from 'common/helpers/rental-property.helper';
import { formatRentalReservationNumber } from 'common/helpers/rental-reservation.helper';
import { IRentalReservationRead } from 'common/services/rental-reservation-service/rental-reservation.service.types';
import styles from '../../rental-portal.module.scss';

const feeTypeIconMap: Record<RentalReservationFeeTypeEnum, React.ReactNode> = {
  [RentalReservationFeeTypeEnum.Standard]: <ClockCircleOutlined />,
  [RentalReservationFeeTypeEnum.Expedited]: <FieldTimeOutlined />,
  [RentalReservationFeeTypeEnum.Express]: <ThunderboltOutlined />,
};

export const rentalReservationsInitialColumns = [
  {
    key: 'id',
    title: 'Reservation #',
    dataKey: 'id',
    width: 120,
    minWidth: 110,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    getValue: (row: IRentalReservationRead) => formatRentalReservationNumber(row.id),
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return (
        <Link className={styles.numberLink} to={`/eiq-manage/rental-portal/reservations/${cell.rowData.id}`}>
          {formatRentalReservationNumber(cell.rowData.id)}
        </Link>
      );
    },
  },
  {
    key: 'address',
    title: 'Property',
    dataKey: 'address',
    width: 180,
    minWidth: 160,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    getValue: (row: IRentalReservationRead) => formatAddressLabel(row.address),
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{formatAddressLabel(cell.rowData.address)}</BaseCellText>;
    },
  },
  {
    key: 'renterName',
    title: 'Renter',
    dataKey: 'renterName',
    width: 135,
    minWidth: 120,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{cell.rowData.renterName}</BaseCellText>;
    },
  },
  {
    key: 'leaseStartDate',
    title: 'Check-in',
    dataKey: 'leaseStartDate',
    width: 95,
    minWidth: 85,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{cell.rowData.leaseStartDate && dayjs(cell.rowData.leaseStartDate).format(DATE_SHORT_MMM_DD)}</BaseCellText>;
    },
  },
  {
    key: 'leaseEndDate',
    title: 'Check-out',
    dataKey: 'leaseEndDate',
    width: 95,
    minWidth: 85,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{cell.rowData.leaseEndDate && dayjs(cell.rowData.leaseEndDate).format(DATE_SHORT_MMM_DD)}</BaseCellText>;
    },
  },
  {
    key: 'managingAgencyName',
    title: 'Agency',
    dataKey: 'managingAgencyName',
    width: 140,
    minWidth: 120,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    getValue: (row: IRentalReservationRead) => formatRentalPropertyAgencyLabel(row.managementType, row.managingAgencyName),
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{formatRentalPropertyAgencyLabel(cell.rowData.managementType, cell.rowData.managingAgencyName)}</BaseCellText>;
    },
  },
  {
    key: 'feeType',
    title: 'Review Priority',
    dataKey: 'feeType',
    width: 135,
    minWidth: 120,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    getValue: (row: IRentalReservationRead) => rentalReservationFeeTypePriorityMap[row.feeType],
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return (
        cell?.rowData?.feeType && (
          <Tag
            className="eiq-tag"
            color={rentalReservationFeeTypeColorMap[cell.rowData.feeType]}
            icon={feeTypeIconMap[cell.rowData.feeType]}>
            {cell.rowData.feeType}
          </Tag>
        )
      );
    },
  },
  {
    key: 'createdAt',
    title: 'Submitted',
    dataKey: 'createdAt',
    width: 115,
    minWidth: 100,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return <BaseCellText>{cell.rowData.createdAt && dayjs(cell.rowData.createdAt).format(DATE_SHORT_MMM_DD_YYYY)}</BaseCellText>;
    },
  },
  {
    key: 'status',
    title: 'Status',
    dataKey: 'status',
    width: 110,
    minWidth: 90,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<IRentalReservationRead>) => {
      return (
        cell?.rowData?.status && (
          <Tag className="eiq-tag" color={rentalReservationStatusColorMap[cell.rowData.status as RentalReservationStatusEnum]}>
            {cell.rowData.status}
          </Tag>
        )
      );
    },
  },
];
